import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConnectionsStore } from '@fry/connections/lib/connections.store';
import {
  Connection,
  connectionTypeNamesById,
  DetailedConnectionType,
  getDefaultConnectionByType
} from '@fry/connections/lib/connection';
import { Observable, of, switchMap } from "rxjs";
import {
  CreateOrUpdateConnectionsDialogComponent
} from "@fry/connections/components/connections-list/create-or-update-connections-dialog.component";
import { NotifyService } from "@fry/lib/utils";
import { DialogComponent } from "@fry/components/common/dialog";
import { isDialogError } from "@fry/components/common/dialog/dialog.error";
import { catchError } from "rxjs/operators";
import { READABLE_MFA_METHODS } from "@fry/lib/auth/account/mfa/mfa";
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'eas-connections-list',
  templateUrl: './connections-list.component.html',
  styleUrls: ['./connections-list.component.css'],
  standalone: false
})
export class ConnectionsListComponent implements OnInit {
  connections: Connection[] = [];
  connectionTypes$: Observable<DetailedConnectionType[]>;

  protected readonly readableName = READABLE_MFA_METHODS;
  protected readonly connectionTypes = connectionTypeNamesById;

  constructor(
    private connectionsStore: ConnectionsStore,
    private dialog: MatDialog,
    private notify: NotifyService,
    private route: ActivatedRoute,
  ) {
    this.connectionTypes$ = this.connectionsStore.getAvailableTypes();
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      let oauthState = params['state'];
      let oauthCode = params['code'];
      if (oauthState) {
        this.completeRegistration(oauthState, oauthCode);
      }
    });
    this.loadConnections();
  }

  completeRegistration(oauthState: string, registrationToken: string): void {
    this.connectionsStore.completeRegistration(oauthState, registrationToken).subscribe({
      next: () => {
        this.notify.success('Registration completed successfully.');
        this.loadConnections();
        window.location.href = window.location.origin + window.location.pathname;
      },
      error: (err) => {
        console.error('Failed to complete registration:', err);
        this.notify.error('An error occurred while completing the registration. Please try again.');
      }
    });
  }

  // Use the store to fetch connections
  loadConnections(): void {
    this.connectionsStore.all(true).subscribe({
      next: (connections) => {
        this.connections = connections;
      },
      error: (err) => {
        console.error('Failed to fetch connections:', err);
        this.notify.error('An error occurred while loading the connections. Please try again.');
      },
    });
  }

  onAddConnection(detailedType: DetailedConnectionType) {
    let connection = getDefaultConnectionByType(detailedType.id)
    this.openCreateOrUpdateDialog(connection, 'create');
  }

  onUpdate(connection: Connection) {
    if (!connection) {
      return;
    }

    this.openCreateOrUpdateDialog(connection, 'update');
  }

   onReplace(connection: Connection) {
    this.openCreateOrUpdateDialog(connection, 'replace');
  }

  private openCreateOrUpdateDialog(connection: Connection, mode: 'create' | 'update' | 'replace') {
    let dialogRef = this.dialog.open(CreateOrUpdateConnectionsDialogComponent, {
      width: '400px',
      data: {
        connection: connection,
        mode: mode,
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.loadConnections();
    });
  }

  onDelete(connection: Connection) {
    const dialog = this.dialog.open(DialogComponent, {
      data: {
        title: $localize`Confirm Deletion`,
        content: $localize`Do you really want to delete this connection? This action cannot be undone.`,
        okButton: $localize`Delete`,
        cancelButton: $localize`Cancel`
      }
    });
    return dialog.afterClosed().pipe(
      switchMap(result => {
        if (!result || isDialogError(result)) {
          return of(null);
        }

        return this.connectionsStore.remove(connection.id).pipe(
          switchMap(() => {
            this.loadConnections();
            this.notify.success('Connection deleted successfully.');
            return of(null);
          }),
          catchError((err) => {
            console.error('Failed to delete connection:', err);
            this.notify.error('An error occurred while deleting the connection. Please try again.');
            return of(null);
          })
        );
      })
    ).subscribe();
  }

  onCancelRegistration(connection: Connection) {
    let state = this.getConnectionState(connection);
    if (state !== 'registering') {
      return;
    }

    this.connectionsStore.cancelRegistration(connection.id).subscribe({
      next: () => {
        this.notify.success('Registration cancelled successfully.');
        this.loadConnections();
      },
      error: (err) => {
        console.error('Failed to cancel registration:', err);
      }
    })
  }

  getConnectionState(connection: Connection): string {
    return connection.oauthState ? 'registering' : 'registered';
  }
}
